/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, Suspense } from 'react';
import { Await, useLocation } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { stripPathPrefix } from '@salesforce/storefront-next-runtime/site-context';
import type { FooterContentData } from '@/lib/api/footer-content.server';
import FooterLegalSection from './footer-legal-section';
import FooterLinksSection from './footer-links-section';
import FooterLogoSection from './footer-logo-section';
import NewsletterSection from './newsletter-section';

export interface MainFooterProps {
    /** Promise resolving to merchant-managed footer content (links + legal). */
    footerContent?: Promise<FooterContentData>;
}

/** Rendered immediately (no links yet) as the `Suspense` fallback and when no promise is provided. */
const EMPTY_FOOTER_CONTENT: FooterContentData = {};

/** Renders the three footer rows — links, logo/country selector, legal + copyright — from resolved content. */
function FooterSections({ links, legal }: FooterContentData): ReactNode {
    return (
        <div className="flex flex-col">
            <FooterLinksSection links={links} />
            <FooterLogoSection />
            <FooterLegalSection legal={legal} />
        </div>
    );
}

export default function MainFooter({ footerContent }: MainFooterProps): ReactElement {
    const location = useLocation();
    const config = useConfig();
    const pathWithoutPrefix = stripPathPrefix({ pathname: location.pathname, prefix: config.url?.prefix || '' });
    const isHomepage = pathWithoutPrefix === '' || pathWithoutPrefix === '/';

    return (
        <footer className="mt-auto">
            {isHomepage && <NewsletterSection />}

            {/* Full-width grey chrome; inner content aligns with the rest of the site via section-container. */}
            <div className="bg-footer-background text-footer-foreground">
                <div className="section-container pt-10 pb-6">
                    {footerContent ? (
                        <Suspense fallback={<FooterSections {...EMPTY_FOOTER_CONTENT} />}>
                            <Await resolve={footerContent} errorElement={<FooterSections {...EMPTY_FOOTER_CONTENT} />}>
                                {(data) => <FooterSections {...data} />}
                            </Await>
                        </Suspense>
                    ) : (
                        <FooterSections {...EMPTY_FOOTER_CONTENT} />
                    )}
                </div>
            </div>
        </footer>
    );
}
